import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import api from "../../services/api";

const EVENT_ORDER = [
  "funnel_register_view",
  "funnel_register_submit",
  "funnel_register_success",
  "funnel_login_view",
  "funnel_login_success",
  "funnel_subscription_view",
  "funnel_checkout_autostart",
  "funnel_checkout_click",
  "funnel_checkout_session_created",
  "funnel_checkout_redirect",
  "funnel_payment_success_view",
  "funnel_premium_activated",
];

export default function AdminAnalyticsFunnel() {
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [counts, setCounts] = useState({});
  const [ratios, setRatios] = useState({});

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.analytics.funnel(days);
        if (!active) return;
        setCounts(res?.data?.counts || {});
        setRatios(res?.data?.ratios || {});
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.error || err?.message || "Failed to load analytics.");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [days]);

  const rows = useMemo(
    () =>
      EVENT_ORDER.map((eventName) => ({
        eventName,
        count: Number(counts?.[eventName] || 0),
      })),
    [counts]
  );

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" fontWeight={800}>
          Subscription Funnel
        </Typography>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="funnel-days-label">Window</InputLabel>
          <Select
            labelId="funnel-days-label"
            label="Window"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {loading ? <CircularProgress /> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      {!loading && !error ? (
        <Stack spacing={2}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>Event</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>Count</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.eventName}>
                    <TableCell>{row.eventName}</TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Conversion Ratios
            </Typography>
            <Typography variant="body2">
              register_view → register_success:{" "}
              <b>{Number(ratios?.register_view_to_success_pct || 0).toFixed(2)}%</b>
            </Typography>
            <Typography variant="body2">
              subscription_view → checkout_redirect:{" "}
              <b>{Number(ratios?.subscription_view_to_checkout_redirect_pct || 0).toFixed(2)}%</b>
            </Typography>
            <Typography variant="body2">
              payment_success_view → premium_activated:{" "}
              <b>{Number(ratios?.payment_success_view_to_premium_activated_pct || 0).toFixed(2)}%</b>
            </Typography>
          </Paper>
        </Stack>
      ) : null}
    </Box>
  );
}
